// Phase 21 (3.1-6) — /admin/shift-requests page.
//
// The request-centric unified operator view: every posting in every state, one
// row each, with a derived merged status. Default filter "Needs attention"
// (matching/unfilled/disputed). Voidable rows (a live booking exists) get a
// "Void & reassign" action that re-posts the request — to the open pool, or to a
// preferred professional who gets first refusal (24h) before the pool. Targeting
// a specific professional with a distance-aware picker also lives on the row's
// detail page (/shift-requests/$id, the manual-match surface).

import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Field, StatusBadge, humanizeEnum, type BadgeTone } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { AdminFilterChips } from '../components/AdminFilterChips';
import { OverrideActionButton } from '../components/OverrideActionButton';
import { adminBeforeLoad } from '../lib/admin-before-load';

// Filter buckets the operator can land on. 'open' = still actively seeking a
// professional (a strict subset of 'needs_attention'); it's the target of the
// dashboard "Open shift requests" card drilldown.
type FilterMode =
  | 'needs_attention'
  | 'open'
  | 'upcoming'
  | 'active'
  | 'done'
  | 'all';

const FILTER_MODES: readonly FilterMode[] = [
  'needs_attention',
  'open',
  'upcoming',
  'active',
  'done',
  'all',
];

export const Route = createFileRoute('/shift-requests/')({
  // The dashboard "Open shift requests" card drills in with `?statusGroup=open`.
  validateSearch: (
    search: Record<string, unknown>,
  ): { statusGroup?: FilterMode } => {
    const g = search['statusGroup'];
    return typeof g === 'string' && (FILTER_MODES as readonly string[]).includes(g)
      ? { statusGroup: g as FilterMode }
      : {};
  },
  beforeLoad: () => adminBeforeLoad('/shift-requests'),
  component: ShiftRequestsPage,
});

interface ShiftRequestRow {
  /** AdminTable row key — mirrors requestId (the API's identifier). */
  id: string;
  requestId: string;
  mergedStatus: string;
  matchingState: string;
  roleCode: string;
  setting: 'LTC' | 'HOMECARE';
  scheduledStart: string;
  scheduledEnd: string;
  facilityId: string | null;
  facilityName: string | null;
  homecareClientId: string | null;
  homecareClientName: string | null;
  recipientLabel: string | null;
  liveShiftId: string | null;
  assignedCaregiverId: string | null;
  assignedCaregiverName: string | null;
  preferredCaregiverId: string | null;
  createdAt: string;
}


// Merged status → StatusBadge tone. Semantic, separate from the app accent.
function toneFor(status: string): BadgeTone {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'IN_PROGRESS':
    case 'FILLED_SCHEDULED':
      return 'forest';
    case 'MATCHING':
    case 'QUEUED':
    case 'NEEDS_SIGNOFF':
      return 'warning';
    case 'DISPUTED':
    case 'UNFILLED_EXPIRED':
    case 'NO_SHOW':
      return 'danger';
    default:
      return 'neutral'; // CANCELLED
  }
}

// A live booking can be voided only while it hasn't run to a terminal state —
// mirrors the backend requireCancellable (SCHEDULED / IN_PROGRESS).
function isVoidable(row: ShiftRequestRow): boolean {
  return (
    row.liveShiftId !== null &&
    (row.mergedStatus === 'FILLED_SCHEDULED' ||
      row.mergedStatus === 'NEEDS_SIGNOFF' ||
      row.mergedStatus === 'IN_PROGRESS')
  );
}

const PAGE_SIZE = 20;

function ShiftRequestsPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [page, setPage] = useState(1);
  // Default to "Needs attention" unless the dashboard "Open shift requests" card
  // sent us here pre-filtered via ?statusGroup=open.
  const [filter, setFilter] = useState<FilterMode>(
    search.statusGroup ?? 'needs_attention',
  );
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['admin', 'shift-requests', { filter, page }],
    queryFn: async () => {
      const query: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
      if (filter !== 'all') query['statusGroup'] = filter;
      const r = (await unwrap(
        api.GET('/api/v1/admin/shift-requests' as never, { params: { query } } as never),
      )) as { items: Omit<ShiftRequestRow, 'id'>[]; totalCount: number };
      // AdminTable keys on `id`; the API's identifier is requestId.
      return { ...r, items: r.items.map((i) => ({ ...i, id: i.requestId })) };
    },
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['admin', 'shift-requests'] });

  const columns: AdminTableColumn<ShiftRequestRow>[] = [
    {
      key: 'scheduledStart',
      header: 'When',
      width: 'w-44',
      render: (r) => new Date(r.scheduledStart).toLocaleString(),
    },
    {
      key: 'roleCode',
      header: 'Role',
      width: 'w-28',
      render: (r) => `${r.roleCode} · ${r.setting}`,
    },
    {
      key: 'facilityName',
      header: 'Buyer',
      render: (r) => r.facilityName ?? r.homecareClientName ?? '—',
    },
    {
      key: 'mergedStatus',
      header: 'Status',
      width: 'w-40',
      render: (r) => (
        <StatusBadge
          status={humanizeEnum(r.mergedStatus)}
          tone={toneFor(r.mergedStatus)}
          // Same shifts the facility sees as "Needs sign-off"; the operator is
          // waiting on the facility, so admin words it from that side.
          {...(r.mergedStatus === 'NEEDS_SIGNOFF' ? { label: 'Awaiting sign-off' } : {})}
        />
      ),
    },
    {
      key: 'assignedCaregiverName',
      header: 'Assigned',
      width: 'w-40',
      render: (r) => r.assignedCaregiverName ?? '—',
    },
    {
      key: 'requestId',
      header: '',
      width: 'w-64',
      render: (r) => (
        <div
          className="flex items-center justify-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {isVoidable(r) && r.liveShiftId && (
            <VoidReassignAction row={r} onDone={invalidate} />
          )}
          <Link
            to="/shift-requests/$id"
            params={{ id: r.requestId }}
            className="text-xs text-forest underline"
            data-testid={`shift-request-detail-${r.requestId}`}
          >
            Details
          </Link>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Shifts</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Every posting in one place. A live booking can be voided + re-posted —
            to everyone, or to a preferred professional who gets first refusal
            before it opens up. Use a row&apos;s Details page to target a specific
            professional with the distance-aware picker.
          </p>
        </header>

        <Card padding="md">
          <CardHeader title="Filters" />
          <div className="mt-3">
            <AdminFilterChips
              value={filter}
              options={
                [
                  ['needs_attention', 'Needs attention'],
                  ['open', 'Open'],
                  ['upcoming', 'Upcoming'],
                  ['active', 'In progress'],
                  ['done', 'Done'],
                  ['all', 'All'],
                ] as const
              }
              onChange={(f) => {
                setFilter(f);
                setPage(1);
              }}
              ariaLabel="Filter shift requests by status"
              testIdPrefix="shift-requests-filter"
            />
          </div>
          <div className="mt-4">
            <AdminTable
              columns={columns}
              rows={listQuery.data?.items ?? []}
              isLoading={listQuery.isPending}
              isError={listQuery.isError}
              onRetry={() => void listQuery.refetch()}
              emptyMessage={
                filter === 'needs_attention'
                  ? 'Nothing needs attention — no unfilled, expired, or disputed postings.'
                  : 'No postings match the current filter.'
              }
              page={page}
              limit={PAGE_SIZE}
              totalCount={listQuery.data?.totalCount ?? 0}
              onPageChange={setPage}
              // Mobile (U8): the right-most Details/Void cell scrolls off a
              // 390px viewport. Make the whole row tap-to-detail so the primary
              // action is always reachable without horizontal-scrolling a hidden
              // column into view. The action cell stops propagation so tapping
              // Void opens its modal instead of navigating.
              onRowClick={(r) =>
                void navigate({
                  to: '/shift-requests/$id',
                  params: { id: r.requestId },
                })
              }
            />
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

// ─── Void & reassign row action ──────────────────────────────────────────────

interface CaregiverOption {
  id: string;
  name: string;
}

function VoidReassignAction({
  row,
  onDone,
}: {
  row: ShiftRequestRow;
  onDone: () => void;
}) {
  // Optional preferred professional — omitted → re-post to the open pool.
  const [preferredId, setPreferredId] = useState('');

  // ACTIVE professionals for the preferred picker (reuses the same list the
  // detail-page picker uses; cached across rows).
  const caregiversQuery = useQuery({
    queryKey: ['admin', 'caregivers', { status: 'ACTIVE', pageSize: 250 }],
    queryFn: async () => {
      const r = (await unwrap(
        api.GET('/api/v1/admin/caregivers' as never, {
          params: { query: { status: 'ACTIVE', pageSize: '250' } },
        } as never),
      )) as { items: { id: string; preferredName: string | null; legalGivenName: string; legalSurname: string }[] };
      return r.items.map(
        (c): CaregiverOption => ({
          id: c.id,
          name: c.preferredName ?? `${c.legalGivenName} ${c.legalSurname}`,
        }),
      );
    },
  });

  const mutation = useMutation({
    mutationFn: async (input: { reason: string }) => {
      await unwrap(
        api.POST('/api/v1/admin/shifts/{id}/void-and-reassign' as never, {
          params: { path: { id: row.liveShiftId } },
          body: {
            reason: input.reason,
            ...(preferredId ? { preferredCaregiverId: preferredId } : {}),
          },
        } as never),
      );
    },
    onSuccess: () => {
      setPreferredId('');
      onDone();
    },
  });

  return (
    <OverrideActionButton
      actionLabel="Void & reassign"
      reasonLabel="Reason for reassigning"
      onConfirm={(input) => mutation.mutateAsync(input)}
      data-testid={`void-reassign-${row.requestId}`}
      instructions={
        <>
          This drops {row.assignedCaregiverName ?? 'the current professional'} from
          the shift and re-posts it. Leave the professional as “everyone” to
          broadcast to the pool, or pick one to give them first refusal for 24
          hours before it opens up. They still have to accept.
        </>
      }
      extraFields={
        <Field label="Preferred professional (optional)">
          <select
            value={preferredId}
            onChange={(e) => setPreferredId(e.target.value)}
            className="w-full rounded-md border border-border bg-cream p-2 text-sm"
            data-testid="void-reassign-preferred"
          >
            <option value="">Re-post to everyone</option>
            {(caregiversQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      }
    >
      Void &amp; reassign
    </OverrideActionButton>
  );
}
